import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ProvisioningStep } from '@/lib/services/dfy';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { Loader2 } from 'lucide-react';

interface ProvisioningStatusTooltipProps {
  children: ReactNode;
  steps?: ProvisioningStep[];
}

const STEP_DISPLAY_NAMES: Record<string, string> = {
  domain_purchase: 'Domain Purchase',
  provider_setup: 'Email Provider Setup',
  provider_subscription: 'Provider Subscription',
  dns_config: 'DNS Configuration',
  domain_verify: 'Domain Verification',
  dkim: 'DKIM Setup',
  email_account: 'Email Account Creation',
  // Backward compat for old orders
  gws_customer: 'Email Provider Setup',
  gws_subscription: 'Provider Subscription',
  gws_user: 'Email Account Creation',
};

function getStepDisplayName(stepName: string): string {
  return STEP_DISPLAY_NAMES[stepName] || stepName;
}

function StepStatusIcon({ status }: { status: ProvisioningStep['status'] }) {
  switch (status) {
    case 'success':
      return <CheckCircleIcon className="h-3.5 w-3.5 text-tooltip-success flex-shrink-0" />;
    case 'failed':
      return <XCircleIcon className="h-3.5 w-3.5 text-tooltip-danger flex-shrink-0" />;
    case 'in_progress':
      return <Loader2 className="h-3.5 w-3.5 text-info-on-dark animate-spin flex-shrink-0" />;
    case 'skipped':
      return <span className="h-3.5 w-3.5 text-tooltip-foreground-muted flex-shrink-0">—</span>;
    default:
      return <div className="h-3.5 w-3.5 rounded-full border-2 border-tooltip-ring flex-shrink-0" />;
  }
}

function getStatusLabel(status: ProvisioningStep['status']): string {
  switch (status) {
    case 'success':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'in_progress':
      return 'In Progress';
    case 'skipped':
      return 'Skipped';
    default:
      return 'Pending';
  }
}

export function ProvisioningStatusTooltip({
  children,
  steps
}: ProvisioningStatusTooltipProps) {
  // Sort steps by a logical order
  const stepOrder = [
    'domain_purchase',
    'provider_setup',
    'gws_customer',
    'provider_subscription',
    'gws_subscription',
    'dns_config',
    'domain_verify',
    'dkim',
    'email_account',
    'gws_user',
  ];

  const sortedSteps = steps ? [...steps].sort((a, b) => {
    const aIndex = stepOrder.indexOf(a.stepName);
    const bIndex = stepOrder.indexOf(b.stepName);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  }) : [];

  // Deduplicate steps by stepName (keep the most recent)
  const uniqueSteps = sortedSteps.reduce((acc, step) => {
    const existing = acc.find(s => s.stepName === step.stepName);
    if (!existing) {
      acc.push(step);
    }
    return acc;
  }, [] as ProvisioningStep[]);

  const hasSteps = uniqueSteps.length > 0;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div className="inline-flex cursor-help">{children}</div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={8}
          className="p-0 w-72 rounded-lg animate-in fade-in-0 zoom-in-95 slide-in-from-left-4 duration-200"
          arrowClassName="!size-3"
        >
          <div className="px-4 py-3 space-y-2.5">
            {hasSteps ? (
              uniqueSteps.map((step) => (
                <div key={step.id} className="flex items-center gap-3">
                  <StepStatusIcon status={step.status} />
                  <span className="text-sm flex-1">
                    {getStepDisplayName(step.stepName)}
                  </span>
                  <span className="text-sm">
                    {getStatusLabel(step.status)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm">Waiting for provisioning to start...</p>
            )}
          </div>
          {uniqueSteps.some(s => s.status === 'failed' && s.errorMessage) && (
            <div className="px-4 py-3 border-t border-tooltip-divider">
              {uniqueSteps
                .filter(s => s.status === 'failed' && s.errorMessage)
                .map(s => (
                  <p key={s.id} className="text-sm">
                    {s.errorMessage}
                  </p>
                ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
